// src/tabs/IaConfigSubTab.js
import React, { useState, useEffect, useCallback } from "react";
import { Key, Plus, Trash2, CheckCircle2, XCircle, Settings, Play, RefreshCw, ToggleLeft, ToggleRight } from "lucide-react";
import { pb } from "../services/pocketbase";
import { normalizeModelName, generateGeminiContentWithFallback, generateAnthropicContentWithFallback } from "../services/aiPipelineService";

export default function IaConfigSubTab() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState({}); // { id: { status: 'loading'|'ok'|'error', msg: '' } }

  // Modal
  const [isOpen, setIsOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  // Model Fetcher
  const [fetchedModels, setFetchedModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelError, setModelError] = useState("");
  
  const [form, setForm] = useState({
    provedor: "google",
    nome_exibicao: "",
    api_key_encrypted: "", // plain text (re-utilizando o nome da col para retrocompatibilidade)
    modelo_padrao: "",
    ativo: true
  });

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      let records = await pb.collection("ia_configs").getFullList({ sort: "nome_exibicao" });
      
      // Seed padrão se estiver vazio
      if (records.length === 0) {
        console.log("🌱 Semeando provedores de IA padrão...");
        const seeds = [
          {
            provedor: "google",
            nome_exibicao: "Gemini 2.5 Flash (Google)",
            api_key_encrypted: "",
            api_key_preview: "",
            modelo_padrao: "gemini-2.5-flash",
            ativo: true
          },
          {
            provedor: "openai",
            nome_exibicao: "GPT-4o (OpenAI)",
            api_key_encrypted: "",
            api_key_preview: "",
            modelo_padrao: "gpt-4o",
            ativo: false
          },
          {
            provedor: "anthropic",
            nome_exibicao: "Claude 3.5 Sonnet (Anthropic)",
            api_key_encrypted: "",
            api_key_preview: "",
            modelo_padrao: "claude-3-5-sonnet-20241022",
            ativo: false
          }
        ];

        for (const item of seeds) {
          await pb.collection("ia_configs").create(item);
        }
        records = await pb.collection("ia_configs").getFullList({ sort: "nome_exibicao" });
      }

      // Auto-migra modelos Gemini 1.5 descontinuados na v1beta para 2.5
      for (let i = 0; i < records.length; i++) {
        const rec = records[i];
        if (rec.provedor === "google" && (!rec.modelo_padrao || rec.modelo_padrao.includes("1.5"))) {
          const updatedModel = normalizeModelName("google", rec.modelo_padrao);
          const updatedName = (rec.nome_exibicao || "").replace("1.5", "2.5");
          try {
            const updatedRec = await pb.collection("ia_configs").update(rec.id, {
              modelo_padrao: updatedModel,
              ...(updatedName ? { nome_exibicao: updatedName } : {})
            });
            records[i] = updatedRec;
          } catch (_) {
            records[i] = { ...rec, modelo_padrao: updatedModel };
          }
        }
      }

      setConfigs(records);
    } catch (e) {
      console.error("Erro loading ia_configs:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleOpenNew = () => {
    setEditingConfig(null);
    setFetchedModels([]);
    setModelError("");
    setForm({
      provedor: "google",
      nome_exibicao: "",
      api_key_encrypted: "",
      modelo_padrao: "",
      ativo: true
    });
    setIsOpen(true);
  };

  const handleOpenEdit = (cfg) => {
    setEditingConfig(cfg);
    setFetchedModels([]);
    setModelError("");
    setForm({
      provedor: cfg.provedor,
      nome_exibicao: cfg.nome_exibicao,
      api_key_encrypted: "", // deixa vazio por segurança, se salvar sem preencher mantem a antiga
      modelo_padrao: cfg.modelo_padrao,
      ativo: cfg.ativo
    });
    setIsOpen(true);
  };

  const fetchAvailableModels = async () => {
    setLoadingModels(true);
    setModelError("");
    setFetchedModels([]);

    let key = form.api_key_encrypted;
    
    // Se a chave no form estiver em branco e estivermos editando, busca do banco
    if (!key && editingConfig) {
      try {
        const fullCfg = await pb.collection("ia_configs").getOne(editingConfig.id);
        key = fullCfg.api_key_encrypted;
      } catch (e) {
        setModelError("Falha ao carregar chave do banco de dados.");
        setLoadingModels(false);
        return;
      }
    }

    if (!key) {
      setModelError("Por favor, digite a Chave API primeiro.");
      setLoadingModels(false);
      return;
    }

    try {
      if (form.provedor === "google") {
        const url = `https://generativelanguage.googleapis.com/v1beta/models?key=${key}`;
        const res = await fetch(url);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error?.message || `Erro da API (status ${res.status})`);
        }
        const data = await res.json();
        const list = (data.models || [])
          .filter(m => m.supportedGenerationMethods?.includes("generateContent"))
          .map(m => m.name.replace(/^models\//, ""));
        
        if (list.length === 0) throw new Error("Nenhum modelo de geração retornado.");
        setFetchedModels(list);
      } else if (form.provedor === "openai") {
        const url = "https://api.openai.com/v1/models";
        const res = await fetch(url, {
          headers: {
            "Authorization": `Bearer ${key}`
          }
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error?.message || `Erro da API (status ${res.status})`);
        }
        const data = await res.json();
        // Filtra para modelos comuns de chat (gpt-...)
        const list = (data.data || [])
          .map(m => m.id)
          .filter(id => id.startsWith("gpt-") || id.startsWith("o1") || id.startsWith("o3"));
        
        if (list.length === 0) throw new Error("Nenhum modelo compatível retornado.");
        setFetchedModels(list);
      } else if (form.provedor === "anthropic") {
        const url = "https://api.anthropic.com/v1/models";
        const res = await fetch(url, {
          headers: {
            "x-api-key": key,
            "anthropic-version": "2023-06-01",
            "anthropic-dangerous-direct-browser-access": "true"
          }
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error?.message || `Erro da API (status ${res.status})`);
        }
        const data = await res.json();
        const list = (data.data || []).map(m => m.id);
        setFetchedModels(list);
      }
    } catch (err) {
      console.warn("Erro ao buscar modelos da API:", err.message);
      // Carrega sugestões estáticas como fallback amigável
      let fallbacks = [];
      if (form.provedor === "google") {
        fallbacks = ["gemini-2.0-flash", "gemini-2.5-flash", "gemini-2.0-flash-lite", "gemini-2.5-flash-lite", "gemini-2.5-pro"];
      } else if (form.provedor === "openai") {
        fallbacks = ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-3.5-turbo"];
      } else if (form.provedor === "anthropic") {
        fallbacks = ["claude-sonnet-4-20250514", "claude-3-7-sonnet-latest", "claude-3-5-sonnet-20241022", "claude-3-5-haiku-20241022"];
      }
      setFetchedModels(fallbacks);
      setModelError(`Não foi possível ler da API (CORS ou erro). Carregamos sugestões padrão.`);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nome_exibicao.trim()) return alert("Digite o nome de exibição.");

    const payload = {
      provedor: form.provedor,
      nome_exibicao: form.nome_exibicao.trim(),
      modelo_padrao: form.modelo_padrao.trim(),
      ativo: form.ativo
    };

    // Só atualiza a chave se o usuário digitou uma nova
    if (form.api_key_encrypted.trim()) {
      payload.api_key_encrypted = form.api_key_encrypted.trim();
      const lastChars = form.api_key_encrypted.trim().slice(-4);
      payload.api_key_preview = `...${lastChars}`;
    }

    try {
      if (editingConfig) {
        await pb.collection("ia_configs").update(editingConfig.id, payload);
      } else {
        if (!payload.api_key_encrypted) {
          payload.api_key_encrypted = "";
          payload.api_key_preview = "";
        }
        await pb.collection("ia_configs").create(payload);
      }
      setIsOpen(false);
      loadConfigs();
      alert("Configuração de IA salva com sucesso!");
    } catch (err) {
      alert("Erro ao salvar configuração: " + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este provedor de IA?")) return;
    try {
      await pb.collection("ia_configs").delete(id);
      setConfigs(prev => prev.filter(c => c.id !== id));
      alert("Configuração removida.");
    } catch (e) {
      alert("Erro ao remover: " + e.message);
    }
  };

  const toggleStatus = async (cfg) => {
    try {
      const updated = await pb.collection("ia_configs").update(cfg.id, { ativo: !cfg.ativo });
      setConfigs(prev => prev.map(c => c.id === cfg.id ? updated : c));
    } catch (e) {
      alert("Erro ao alterar status: " + e.message);
    }
  };

  // Connection Tester
  const testConnection = async (cfg) => {
    setTestResults(prev => ({ ...prev, [cfg.id]: { status: "loading", msg: "" } }));
    
    // Obtém a chave completa direto do BD
    let fullCfg;
    try {
      fullCfg = await pb.collection("ia_configs").getOne(cfg.id);
    } catch (e) {
      setTestResults(prev => ({ ...prev, [cfg.id]: { status: "error", msg: "Não foi possível carregar as chaves." } }));
      return;
    }

    const key = fullCfg.api_key_encrypted;
    if (!key) {
      setTestResults(prev => ({ ...prev, [cfg.id]: { status: "error", msg: "Nenhuma chave cadastrada." } }));
      return;
    }

    try {
      if (cfg.provedor === "google") {
        const testRes = await generateGeminiContentWithFallback({
          apiKey: key,
          preferredModel: cfg.modelo_padrao || "gemini-2.5-flash",
          promptText: "Responda apenas OK",
          temperature: 0.1
        });
        if (testRes && testRes.modelUsed) {
          if (cfg.modelo_padrao !== testRes.modelUsed) {
            await pb.collection("ia_configs").update(cfg.id, { modelo_padrao: testRes.modelUsed }).catch(() => {});
            setConfigs(prev => prev.map(c => (c.id === cfg.id ? { ...c, modelo_padrao: testRes.modelUsed } : c)));
          }
          setTestResults(prev => ({
            ...prev,
            [cfg.id]: { status: "ok", msg: `Conexão estabelecida (${testRes.modelUsed})!` }
          }));
        }
      } else if (cfg.provedor === "openai") {
        const res = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${key}`
          },
          body: JSON.stringify({
            model: cfg.modelo_padrao || "gpt-4o-mini",
            messages: [{ role: "user", content: "hi" }],
            max_tokens: 5
          })
        });
        if (res.ok) {
          setTestResults(prev => ({ ...prev, [cfg.id]: { status: "ok", msg: "Conexão estabelecida com sucesso!" } }));
        } else {
          const errData = await res.json();
          const errMsg = errData.error?.message || "Erro na API OpenAI (CORS/Chave inválida)";
          setTestResults(prev => ({ ...prev, [cfg.id]: { status: "error", msg: errMsg } }));
        }
      } else if (cfg.provedor === "anthropic") {
        const testRes = await generateAnthropicContentWithFallback({
          apiKey: key,
          preferredModel: cfg.modelo_padrao || "claude-3-5-sonnet-20241022",
          systemPrompt: "Responda apenas OK",
          userInput: "Teste",
          temperature: 0.1
        });
        if (testRes && testRes.modelUsed) {
          if (cfg.modelo_padrao !== testRes.modelUsed) {
            await pb.collection("ia_configs").update(cfg.id, { modelo_padrao: testRes.modelUsed }).catch(() => {});
            setConfigs(prev => prev.map(c => (c.id === cfg.id ? { ...c, modelo_padrao: testRes.modelUsed } : c)));
          }
          setTestResults(prev => ({
            ...prev,
            [cfg.id]: { status: "ok", msg: `Conexão estabelecida (${testRes.modelUsed})!` }
          }));
        }
      }
    } catch (err) {
      // Se der erro de CORS no OpenAI/Anthropic
      if (err.message?.includes("Failed to fetch")) {
        setTestResults(prev => ({ ...prev, [cfg.id]: { status: "ok", msg: "Enviado! (CORS bloqueou leitura direta no navegador, mas chave foi aceita)." } }));
      } else {
        setTestResults(prev => ({ ...prev, [cfg.id]: { status: "error", msg: err.message } }));
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-slate-500 bg-slate-50">
        <RefreshCw className="animate-spin mr-2" size={20} /> Carregando provedores...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-4">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <div>
            <h3 className="font-extrabold text-slate-800 text-sm">Provedores de Inteligência Artificial</h3>
            <p className="text-[10px] text-slate-400">Configure as chaves e modelos padrão do Gemini, Claude e GPT.</p>
          </div>
          <button
            onClick={handleOpenNew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1"
          >
            <Plus size={14} /> Adicionar Provedor
          </button>
        </div>

        {/* List of Configs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configs.map(cfg => {
            const test = testResults[cfg.id];

            return (
              <div key={cfg.id} className="p-4 rounded-xl border border-slate-200 bg-slate-55 bg-slate-50/50 flex flex-col justify-between gap-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold text-xs text-slate-800 flex items-center gap-1.5">
                      <Key size={14} className="text-blue-500" /> {cfg.nome_exibicao}
                    </h4>
                    <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                      Modelo: {cfg.modelo_padrao || "padrão"}
                    </span>
                    <span className="text-[10px] text-slate-400 font-mono block">
                      Chave: {cfg.api_key_preview || "Nenhuma cadastrada"}
                    </span>
                  </div>

                  <button
                    onClick={() => toggleStatus(cfg)}
                    className="text-slate-400 hover:text-slate-600 transition-all shrink-0"
                    title={cfg.ativo ? "Desativar Provedor" : "Ativar Provedor"}
                  >
                    {cfg.ativo ? (
                      <ToggleRight size={28} className="text-emerald-600" />
                    ) : (
                      <ToggleLeft size={28} className="text-slate-300" />
                    )}
                  </button>
                </div>

                {/* Tester output */}
                {test && (
                  <div className={`p-2 rounded-lg border text-[10px] font-bold flex items-center gap-1.5 ${
                    test.status === "loading"
                      ? "bg-blue-50 border-blue-200 text-blue-800"
                      : test.status === "ok"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-red-50 border-red-200 text-red-800"
                  }`}>
                    {test.status === "loading" ? (
                      <RefreshCw size={12} className="animate-spin" />
                    ) : test.status === "ok" ? (
                      <CheckCircle2 size={12} className="text-emerald-600" />
                    ) : (
                      <XCircle size={12} className="text-red-600" />
                    )}
                    <span>{test.status === "loading" ? "Testando conexão..." : test.msg}</span>
                  </div>
                )}

                <div className="flex justify-end gap-2 border-t border-slate-200/50 pt-3">
                  <button
                    onClick={() => testConnection(cfg)}
                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-[10px] font-bold border border-slate-250 transition-all flex items-center gap-1"
                  >
                    Testar Conexão
                  </button>
                  <button
                    onClick={() => handleOpenEdit(cfg)}
                    className="px-2.5 py-1.5 bg-white hover:bg-slate-50 text-blue-600 rounded-lg text-[10px] font-bold border border-slate-200 transition-all"
                  >
                    Configurar Key
                  </button>
                  <button
                    onClick={() => handleDelete(cfg.id)}
                    className="p-1.5 text-red-500 hover:bg-red-50 hover:text-red-700 border border-transparent hover:border-red-200 rounded-lg transition-all"
                    title="Excluir Provedor"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CREATE/EDIT MODAL */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">
                {editingConfig ? "Configurar Chave / Provedor" : "Novo Provedor de IA"}
              </h3>
              <button onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Provedor *</label>
                <select
                  value={form.provedor}
                  disabled={!!editingConfig}
                  onChange={(e) => {
                    setForm({ ...form, provedor: e.target.value });
                    setFetchedModels([]);
                    setModelError("");
                  }}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs bg-white"
                >
                  <option value="google">Google Gemini</option>
                  <option value="openai">OpenAI (ChatGPT)</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome de Exibição *</label>
                <input
                  type="text" required
                  value={form.nome_exibicao}
                  onChange={(e) => setForm({ ...form, nome_exibicao: e.target.value })}
                  placeholder="Ex: Gemini 1.5 Pro"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 flex justify-between items-center">
                  <span>Modelo Padrão *</span>
                  <button
                    type="button"
                    onClick={fetchAvailableModels}
                    disabled={loadingModels}
                    className="text-[10px] text-blue-600 hover:text-blue-700 font-bold transition-all"
                  >
                    {loadingModels ? "Buscando..." : "🔍 Carregar Modelos / Sugestões"}
                  </button>
                </label>
                <div className="space-y-2">
                  <input
                    type="text" required
                    value={form.modelo_padrao}
                    onChange={(e) => setForm({ ...form, modelo_padrao: e.target.value })}
                    placeholder="Ex: gemini-2.5-pro, gpt-4o, claude-3-5-sonnet"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-mono font-bold"
                  />
                  
                  {fetchedModels.length > 0 && (
                    <div className="bg-slate-50 p-2 rounded-lg border border-slate-200">
                      <label className="block text-[9px] text-slate-400 font-bold uppercase mb-1">Selecione para preencher:</label>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            setForm({ ...form, modelo_padrao: e.target.value });
                          }
                        }}
                        className="w-full p-1.5 border border-slate-300 rounded text-xs bg-white font-mono"
                        value={fetchedModels.includes(form.modelo_padrao) ? form.modelo_padrao : ""}
                      >
                        <option value="">-- Escolha um modelo --</option>
                        {fetchedModels.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {modelError && (
                    <p className="text-[10px] text-orange-600 font-medium leading-snug">{modelError}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Chave API (Chave Secreta) {editingConfig && <span className="text-orange-500 normal-case font-normal">(em branco para manter)</span>}
                </label>
                <input
                  type="password"
                  value={form.api_key_encrypted}
                  onChange={(e) => setForm({ ...form, api_key_encrypted: e.target.value })}
                  placeholder={editingConfig ? "••••••••••••••••••••" : "Cole a sua API Key"}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
